import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Building, Search, Upload, Loader2, MapPin, 
  User, Phone, Mail, Bed, Bath, PoundSterling, FileText,
  AlertTriangle, CheckCircle, Calendar
} from "lucide-react";
import { API } from "@/App";

export const PropertiesPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  // Initialise from URL so returning from a property detail keeps the search
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('q') || "");
  const [importing, setImporting] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [propertyModal, setPropertyModal] = useState(false);
  
  // Keep URL in sync with the search term so the back button restores it
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (searchTerm) next.set('q', searchTerm); else next.delete('q');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data.properties || []);
    } catch (error) {
      console.error("Error fetching properties:", error);
      toast.error("Failed to load properties");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/properties/import`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success(`Imported ${response.data.imported} new, updated ${response.data.updated} properties`);
      fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const openPropertyDetail = async (property) => {
    try {
      const response = await axios.get(`${API}/properties/${property.property_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedProperty(response.data);
      setPropertyModal(true);
    } catch (error) {
      toast.error("Failed to load property details");
    }
  };

  const filteredProperties = properties.filter(p => 
    p.display_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.postcode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.landlord_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const statusColors = {
    "Let": "bg-green-100 text-green-700",
    "Let Agreed": "bg-blue-100 text-blue-700",
    "On The Market": "bg-amber-100 text-amber-700",
    "Off The Market": "bg-slate-100 text-slate-700"
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/admin")}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-semibold font-outfit">Properties</h1>
                <p className="text-sm text-muted-foreground">{properties.length} properties from 10/90</p>
              </div>
            </div>
            
            {user.role === "admin" && (
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept=".xls,.xlsx,.csv"
                  className="hidden"
                />
                <Button 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importing}
                  data-testid="import-properties-btn"
                >
                  {importing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Import from 10/90
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Search */}
        <div className="relative max-w-md mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by address, postcode, or landlord..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="search-properties"
          />
        </div>

        {/* Properties Grid */}
        {filteredProperties.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Building className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-muted-foreground">
                {properties.length === 0 
                  ? "No properties imported yet. Click 'Import from 10/90' to get started."
                  : "No properties match your search"
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProperties.map((property) => (
              <Card 
                key={property.property_id}
                className="cursor-pointer hover:border-primary/50 hover:shadow-md transition-all"
                onClick={() => openPropertyDetail(property)}
                data-testid={`property-${property.reference}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <Badge variant="outline" className="text-xs">
                      #{property.reference}
                    </Badge>
                    <Badge className={statusColors[property.status] || "bg-slate-100"}>
                      {property.status}
                    </Badge>
                  </div>
                  
                  <h3 className="font-semibold mb-1 line-clamp-2">{property.display_address}</h3>
                  
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mb-3">
                    <MapPin className="w-3 h-3" />
                    {property.postcode}
                  </p>

                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                    {property.bedrooms !== null && (
                      <span className="flex items-center gap-1">
                        <Bed className="w-4 h-4" />
                        {property.bedrooms}
                      </span>
                    )}
                    {property.bathrooms !== null && (
                      <span className="flex items-center gap-1">
                        <Bath className="w-4 h-4" />
                        {property.bathrooms}
                      </span>
                    )}
                    {property.property_type && (
                      <span>{property.property_type}</span>
                    )}
                  </div>

                  <div className="pt-3 border-t">
                    <div className="flex items-center justify-between">
                      <div className="text-sm">
                        <p className="text-muted-foreground">Landlord</p>
                        <p className="font-medium truncate max-w-[150px]">{property.landlord_name || "—"}</p>
                      </div>
                      {property.rent_amount && (
                        <div className="text-right">
                          <p className="text-muted-foreground text-sm">Rent</p>
                          <p className="font-semibold text-primary">£{property.rent_amount}/mo</p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Property Detail Modal */}
      <Dialog open={propertyModal} onOpenChange={setPropertyModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedProperty && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Building className="w-5 h-5" />
                  #{selectedProperty.property?.reference}
                </DialogTitle>
                <DialogDescription>
                  {selectedProperty.property?.display_address}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Property Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Type</p>
                    <p className="font-medium">{selectedProperty.property?.property_type || "—"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <Badge className={statusColors[selectedProperty.property?.status]}>
                      {selectedProperty.property?.status}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Bedrooms</p>
                    <p className="font-medium">{selectedProperty.property?.bedrooms || "—"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Bathrooms</p>
                    <p className="font-medium">{selectedProperty.property?.bathrooms || "—"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Rent</p>
                    <p className="font-medium text-lg">
                      £{selectedProperty.property?.rent_amount || "—"}/month
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">EPC Band</p>
                    <p className="font-medium">{selectedProperty.property?.epc_band || "—"}</p>
                  </div>
                </div>

                {/* Landlord */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Landlord
                  </h4>
                  <div className="space-y-2">
                    <p className="font-medium">{selectedProperty.property?.landlord_name}</p>
                    {selectedProperty.property?.landlord_company && (
                      <p className="text-sm text-muted-foreground">
                        {selectedProperty.property.landlord_company}
                      </p>
                    )}
                    {selectedProperty.property?.landlord_email && (
                      <p className="text-sm flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        {selectedProperty.property.landlord_email}
                      </p>
                    )}
                    {selectedProperty.property?.landlord_phone && (
                      <p className="text-sm flex items-center gap-2">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        {selectedProperty.property.landlord_phone}
                      </p>
                    )}
                  </div>
                </div>

                {/* Compliance */}
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Compliance
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-muted-foreground">Gas Certificate</p>
                      <p className="font-medium text-sm">{selectedProperty.property?.gas_cert_expiry || "N/A"}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-muted-foreground">Electrical Certificate</p>
                      <p className="font-medium text-sm">{selectedProperty.property?.electrical_cert_expiry || "N/A"}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-muted-foreground">EPC Expiry</p>
                      <p className="font-medium text-sm">{selectedProperty.property?.epc_expiry || "N/A"}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-muted-foreground">Management</p>
                      <p className="font-medium text-sm">{selectedProperty.property?.management_type || "N/A"}</p>
                    </div>
                  </div>
                </div>

                {/* Jobs */}
                {selectedProperty.jobs?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3">Recent Jobs ({selectedProperty.jobs.length})</h4>
                    <div className="space-y-2">
                      {selectedProperty.jobs.slice(0, 3).map((job) => (
                        <div key={job.job_id} className="p-3 bg-slate-50 rounded-lg flex items-center justify-between">
                          <span className="text-sm">{job.job_id.slice(-8).toUpperCase()}</span>
                          <Badge>{job.status}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Inspections */}
                {selectedProperty.inspections?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3">Inspections ({selectedProperty.inspections.length})</h4>
                    <div className="space-y-2">
                      {selectedProperty.inspections.slice(0, 3).map((insp) => (
                        <div key={insp.inspection_id} className="p-3 bg-slate-50 rounded-lg flex items-center justify-between">
                          <div>
                            <span className="text-sm font-medium">{insp.inspection_type}</span>
                            <p className="text-xs text-muted-foreground">{insp.scheduled_date}</p>
                          </div>
                          <Badge>{insp.status}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => {
                      setPropertyModal(false);
                      navigate(`/admin/inspections/new?property=${selectedProperty.property?.reference}`);
                    }}
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule Inspection
                  </Button>
                  <Button 
                    className="flex-1"
                    onClick={() => {
                      setPropertyModal(false);
                      navigate(`/tenant/report?property=${selectedProperty.property?.reference}`);
                    }}
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Report Issue
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
